/**
 * Command signing (docs/signing.md §5–6).
 *
 * Every command is a SignedCommand: a JCS envelope signed by this device's non-extractable key.
 * `envelope.id` is the idempotency key — a retry re-sends the *same* signed command, so the
 * daemon executes it at most once. Risky kinds (land.merge, land.deploy, secret.*, …) also carry
 * a fresh passkey assertion over `createStepUpChallenge(envelope)`, binding the tap to exactly
 * this command, machine, payload and expiry.
 */
import {
  type ApprovalBatchItem,
  acceptsStatementStepUp,
  approvalBatchItem,
  createApprovalBatchChallenge,
  createStepUpChallenge,
  DEFAULT_COMMAND_TTL_MS,
  type DeviceEndorsement,
  DOMAIN,
  type JsonValue,
  MAX_RISKY_COMMAND_TTL_MS,
  type SignedCommand,
  signCommand,
  signEncryptedCommand,
  type WebCryptoKey,
} from '@viko/crypto'
import type { LandGrantProof } from '@viko/protocol'
import {
  type CommandKind,
  type CommandPayload,
  isRiskyCommandKind,
  parseCommandPayload,
} from '@viko/protocol'
import { clock } from './clock.js'
import type { PasskeyProvider } from './passkeys.js'

export interface SigningDevice {
  deviceKeyId: string
  privateKey: CryptoKey
  endorsement?: DeviceEndorsement
}

/** passkey-budget WP3: a risky command waiting in the approval tray (signed only on approve). */
export interface TrayRequest {
  kind: CommandKind
  machineId: string
  payload: unknown
  opts: SignOptions
  summary: string
  production: boolean
}
/** The approval tray as the signer sees it: queue a risky command until the user approves. */
export interface ApprovalTrayPort {
  request(r: TrayRequest): Promise<SignedCommand>
}

/** What the step-up prompt shows before the passkey sheet (WebAuthn has no "what you sign"). */
export interface StepUpRequest {
  kind: string
  machineId: string
  summary: string
  expiresAt: number
}

export interface SignOptions {
  /** Human summary for the step-up prompt, e.g. "Land fix/backoff into main". */
  summary?: string
  /**
   * passkey-budget WP3: a production release. The approval tray lists it as its own row and calls
   * it out ("includes 1 production release"); it may share the tray's one tap.
   */
  production?: boolean
  /**
   * land-grant: a standing landing grant for this project. A `land.merge` (never "land anyway")
   * carries it instead of a fresh passkey tap; the daemon decides whether it covers the landing.
   */
  landGrant?: LandGrantProof
  ttlMs?: number
  /** Re-use an id (tests); by default a fresh UUID. */
  id?: string
}

export class CommandSigner {
  constructor(
    private readonly deps: {
      device: () => Promise<SigningDevice | undefined>
      content?: (
        machineId: string,
      ) => Promise<{ accountId: string; epoch: number; key: WebCryptoKey }>
      passkeys: PasskeyProvider
      /** Shows the step-up context and resolves when the user chooses to continue. */
      confirmStepUp?: (req: StepUpRequest) => Promise<void>
      /**
       * passkey-budget WP3: risky commands wait in the approval tray instead (one tap approves
       * every pending item). Without a tray: `confirmStepUp` + one tap per command.
       */
      tray?: ApprovalTrayPort
      now?: () => number
      rpId?: string
    },
  ) {}

  private now() {
    return (this.deps.now ?? clock.now)()
  }

  isRisky(kind: string) {
    return isRiskyCommandKind(kind)
  }

  async sign<K extends CommandKind>(
    kind: K,
    machineId: string,
    payload: CommandPayload<K>,
    opts: SignOptions = {},
  ): Promise<SignedCommand> {
    const granted = withLandGrant(kind, payload, opts)
    if (granted)
      return (await this.prepare(kind, machineId, granted as CommandPayload<K>, opts)).signed
    // passkey-budget WP3: a risky command waits in the tray; it is signed only when approved
    // (so its 5-minute expiry starts then), together with everything else pending.
    if (
      this.deps.tray &&
      this.isRisky(kind) &&
      !(acceptsStatementStepUp(kind) && hasStatementApproval(payload))
    ) {
      const parsed = parseCommandPayload(kind, payload)
      if (!parsed.ok) throw new SignError('invalid-payload', parsed.error ?? `bad ${kind} payload`)
      return this.deps.tray.request({
        kind,
        machineId,
        payload,
        opts,
        summary: opts.summary ?? kind,
        production: isProductionRequest(kind, opts),
      })
    }
    const { signed, risky } = await this.prepare(kind, machineId, payload, opts)
    if (!risky) return signed
    // account-projects: a checkout carries its own passkey-approved statement (one tap for every
    // machine); the daemon verifies it as this command's step-up.
    if (acceptsStatementStepUp(kind) && hasStatementApproval(payload)) return signed
    await this.deps.confirmStepUp?.({
      kind,
      machineId,
      summary: opts.summary ?? kind,
      expiresAt: signed.envelope.expiresAt,
    })
    const challenge = await createStepUpChallenge(signed.envelope)
    const stepUp = await this.deps.passkeys.assert(challenge, {
      ...(this.deps.rpId ? { rpId: this.deps.rpId } : {}),
    })
    return { ...signed, stepUp }
  }

  /**
   * Batched step-up (docs/e2e.md "Fewer passkey ceremonies"): sign several landings, then ONE
   * passkey assertion over the batch of their step-up challenges. Each command keeps its own
   * device signature, nonce and expiry; each daemon verifies only its own item. Only `land.merge`
   * is batchable (production ships and secrets always take their own tap).
   */
  async signBatch(
    commands: { machineId: string; payload: CommandPayload<'land.merge'>; opts?: SignOptions }[],
    summary: string,
  ): Promise<SignedCommand[]> {
    if (commands.length === 0) return []
    // passkey-budget WP3: with a tray, each landing joins it (one row per branch) and shares the
    // tray's one tap with anything else pending; grant-covered ones still need none.
    if (this.deps.tray)
      return Promise.all(
        commands.map((c) =>
          this.sign('land.merge', c.machineId, c.payload, {
            ...c.opts,
            summary: `Land ${c.payload.branch ?? 'this branch'} into main`,
          }),
        ),
      )
    // land-grant: landings a grant covers need no ceremony; only the rest share one tap.
    const tapped = commands.filter((c) => !withLandGrant('land.merge', c.payload, c.opts ?? {}))
    if (tapped.length < commands.length) {
      const signedTapped = await this.signBatch(tapped, summary)
      const out: SignedCommand[] = []
      let t = 0
      for (const c of commands)
        out.push(
          withLandGrant('land.merge', c.payload, c.opts ?? {})
            ? await this.sign('land.merge', c.machineId, c.payload, c.opts)
            : (signedTapped[t++] as SignedCommand),
        )
      return out
    }
    if (commands.length === 1) {
      const [c] = commands
      return [await this.sign('land.merge', c!.machineId, c!.payload, c!.opts)]
    }
    if (commands.length > MAX_BATCH)
      throw new SignError('invalid-payload', `Land at most ${MAX_BATCH} branches at once.`)
    const signed: SignedCommand[] = []
    for (const c of commands)
      signed.push((await this.prepare('land.merge', c.machineId, c.payload, c.opts ?? {})).signed)
    await this.deps.confirmStepUp?.({
      kind: 'land.merge',
      machineId: [...new Set(commands.map((c) => c.machineId))].join(', '),
      summary,
      expiresAt: Math.min(...signed.map((s) => s.envelope.expiresAt)),
    })
    const batch: ApprovalBatchItem[] = []
    for (const s of signed)
      batch.push(approvalBatchItem(DOMAIN.stepUp, await createStepUpChallenge(s.envelope)))
    const stepUp = await this.deps.passkeys.assert(await createApprovalBatchChallenge(batch), {
      ...(this.deps.rpId ? { rpId: this.deps.rpId } : {}),
    })
    return signed.map((s) => ({ ...s, stepUp, stepUpBatch: batch }))
  }

  /**
   * passkey-budget WP3: sign the tray's approved commands with ONE passkey ceremony. Each keeps
   * its own device signature, nonce and ≤ 5 min expiry; one command takes a plain step-up, several
   * share one assertion over the batch of their step-up challenges (`viko/approval-batch/v1`), so
   * each daemon verifies only its own item. Production releases may be in the batch.
   */
  async signTapped(reqs: readonly TrayRequest[]): Promise<SignedCommand[]> {
    if (reqs.length === 0) return []
    if (reqs.length > MAX_BATCH)
      throw new SignError('invalid-payload', `Approve at most ${MAX_BATCH} actions at once.`)
    const signed: SignedCommand[] = []
    for (const r of reqs)
      signed.push((await this.prepare(r.kind, r.machineId, r.payload as never, r.opts)).signed)
    const rp = this.deps.rpId ? { rpId: this.deps.rpId } : {}
    if (signed.length === 1) {
      const one = signed[0] as SignedCommand
      const stepUp = await this.deps.passkeys.assert(await createStepUpChallenge(one.envelope), rp)
      return [{ ...one, stepUp }]
    }
    const batch: ApprovalBatchItem[] = []
    for (const s of signed)
      batch.push(approvalBatchItem(DOMAIN.stepUp, await createStepUpChallenge(s.envelope)))
    const stepUp = await this.deps.passkeys.assert(await createApprovalBatchChallenge(batch), rp)
    return signed.map((s) => ({ ...s, stepUp, stepUpBatch: batch }))
  }

  private async prepare<K extends CommandKind>(
    kind: K,
    machineId: string,
    payload: CommandPayload<K>,
    opts: SignOptions,
  ): Promise<{ signed: SignedCommand; risky: boolean }> {
    const parsed = parseCommandPayload(kind, payload)
    if (!parsed.ok) throw new SignError('invalid-payload', parsed.error ?? `bad ${kind} payload`)
    const device = await this.deps.device()
    if (!device) throw new SignError('no-device', 'This device has no signing key yet.')
    if (!device.endorsement)
      throw new SignError('unendorsed', 'Trust this device with a passkey before sending commands.')
    const risky = this.isRisky(kind)
    const ttlMs = risky
      ? Math.min(opts.ttlMs ?? MAX_RISKY_COMMAND_TTL_MS, MAX_RISKY_COMMAND_TTL_MS)
      : (opts.ttlMs ?? DEFAULT_COMMAND_TTL_MS)
    const content = await this.deps.content?.(machineId)
    const input = {
      machineId,
      kind,
      payload: payload as JsonValue,
      riskLevel: risky ? ('risky' as const) : ('normal' as const),
      ttlMs,
      now: this.now(),
      ...(opts.id ? { id: opts.id } : {}),
    }
    const key = { deviceKeyId: device.deviceKeyId, privateKey: device.privateKey }
    const approval = { endorsement: device.endorsement }
    const signed = content
      ? await signEncryptedCommand(input, key, content, approval)
      : await signCommand(input, key, approval)
    return { signed, risky }
  }
}

const MAX_BATCH = 32
/** passkey-budget WP3: the tray's per-tap limit (the approval-batch item cap). */
export const MAX_TRAY_BATCH = MAX_BATCH

function isProductionRequest(kind: string, opts: SignOptions): boolean {
  return opts.production === true || kind.startsWith('release.')
}

/** land-grant: the payload with the grant attached, when one may stand in for the tap. */
function withLandGrant(kind: string, payload: unknown, opts: SignOptions): unknown {
  if (kind !== 'land.merge' || !opts.landGrant) return undefined
  const p = payload as { acceptBaselineFailures?: boolean }
  if (p.acceptBaselineFailures) return undefined // "land anyway" always takes a fresh tap
  return { ...(payload as object), landGrant: opts.landGrant }
}

function hasStatementApproval(payload: unknown): boolean {
  const p = payload as { statement?: unknown; assertion?: unknown } | null
  return !!p && typeof p.statement === 'object' && typeof p.assertion === 'object'
}

export class SignError extends Error {
  constructor(
    readonly code: 'invalid-payload' | 'no-device' | 'unendorsed' | 'cancelled',
    message: string,
  ) {
    super(message)
  }
}
